import type { HttpClient } from "./httpClient";

export type NewsletterStatus =
  | "pending"
  | "subscribed"
  | "unsubscribed"
  | "bounced";
export type NewsletterFilters = {
  q?: string;
  status?: NewsletterStatus | "";
  consent?: "eligible" | "missing" | "";
  page?: number;
  pageSize?: number;
};
export type NewsletterPage<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};
export type NewsletterSubscriber = {
  id: string;
  email: string;
  name?: string | null;
  status: NewsletterStatus;
  marketingConsent: boolean;
  eligible: boolean;
  consentSource: string;
  consentEvidence?: string | null;
  consentAtUtc?: string | null;
  confirmedAtUtc?: string | null;
  withdrawnAtUtc?: string | null;
  createdAt: string;
  updatedAtUtc?: string | null;
  version: number;
};
export type NewsletterDetail = {
  subscriber: NewsletterSubscriber;
  history: NewsletterPage<{
    id: string;
    action: string;
    source: string;
    actor?: string;
    evidence?: string;
    evidenceAtUtc?: string;
    createdAtUtc: string;
  }>;
  deliveries: {
    id: string;
    kind: string;
    status: string;
    attempts: number;
    error?: string;
    createdAtUtc: string;
  }[];
};
export type NewsletterChange = {
  status: string;
  version: number;
  reason: string;
  consentAtUtc?: string;
  consentSource?: string;
  consentEvidence?: string;
};
export type NewsletterImport = {
  id: string;
  status: string;
  expiresAtUtc: string;
  total: number;
  applied: number;
  skipped: number;
  failed: number;
  rows: {
    row: number;
    email: string;
    status: string;
    outcome: string;
    proposedAction?: string;
    error?: string;
  }[];
};
export function newsletterQuery(filters: NewsletterFilters) {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  return query.toString();
}
export function createNewsletterClient(client: HttpClient) {
  const base = "/api/admin/newsletter";
  return {
    listNewsletterSubscribers: (
      filters: NewsletterFilters = {},
      tenantId?: string,
    ) =>
      client.get<NewsletterPage<NewsletterSubscriber>>(
        `${base}/subscribers?${newsletterQuery(filters)}`,
        { tenantId },
      ),
    getNewsletterSubscriber: (id: string, page: number, tenantId?: string) =>
      client.get<NewsletterDetail>(
        `${base}/subscribers/${encodeURIComponent(id)}?page=${page}`,
        { tenantId },
      ),
    changeNewsletterSubscriber: (
      id: string,
      change: NewsletterChange,
      tenantId?: string,
    ) =>
      client.post<NewsletterSubscriber>(
        `${base}/subscribers/${encodeURIComponent(id)}/status`,
        change,
        { tenantId },
      ),
    deleteNewsletterSubscriber: (id: string, tenantId?: string) =>
      client.delete<{ ok: true }>(
        `${base}/subscribers/${encodeURIComponent(id)}`,
        undefined,
        { tenantId },
      ),
    exportNewsletterSubscribers: (
      filters: NewsletterFilters,
      mode: "consented" | "records",
      tenantId?: string,
    ) =>
      client.get<string>(
        `${base}/subscribers/export?${newsletterQuery(filters)}&mode=${mode}`,
        { tenantId, timeoutMs: 120_000 },
      ),
    newsletterImportTemplate: (tenantId?: string) =>
      client.get<string>(`${base}/imports/template`, { tenantId }),
    previewNewsletterImport: (file: File, tenantId?: string) => {
      const form = new FormData();
      form.append("file", file);
      return client.post<NewsletterImport>(`${base}/imports/preview`, form, {
        tenantId,
        timeoutMs: 120_000,
      });
    },
    getNewsletterImport: (id: string, tenantId?: string) =>
      client.get<NewsletterImport>(
        `${base}/imports/${encodeURIComponent(id)}`,
        { tenantId },
      ),
    applyNewsletterImport: (id: string, tenantId?: string) =>
      client.post<NewsletterImport>(
        `${base}/imports/${encodeURIComponent(id)}/apply`,
        {},
        { tenantId, timeoutMs: 120_000 },
      ),
  };
}
